import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "../Reveal";
import { PORTFOLIO } from "../../data/content";

// Abstract, image-free generative art per project (emerald + gold geometry)
const PALETTES = [
  { a: "#0B3D2E", b: "#D4AF37" },
  { a: "#141414", b: "#F5E6A3" },
  { a: "#0B3D2E", b: "#F5E6A3" },
  { a: "#1a1305", b: "#D4AF37" },
  { a: "#0a2a20", b: "#D4AF37" },
  { a: "#141414", b: "#D4AF37" },
];

function AbstractArt({ index, title }) {
  const p = PALETTES[index % PALETTES.length];
  const initials = title.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: `radial-gradient(120% 120% at 20% 10%, ${p.a} 0%, #0A0A0A 70%)` }}>
      {/* concentric gold rings */}
      <svg viewBox="0 0 400 400" className="absolute -right-16 -top-16 w-80 h-80 opacity-70">
        <defs>
          <linearGradient id={`pg-${index}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F5E6A3" />
            <stop offset="100%" stopColor="#997A15" />
          </linearGradient>
        </defs>
        {[150, 115, 80, 45].map((r, i) => (
          <circle key={i} cx="200" cy="200" r={r} fill="none" stroke={`url(#pg-${index})`} strokeWidth={i === 1 ? 2 : 0.8} opacity={0.5 - i * 0.08} />
        ))}
      </svg>
      {/* diagonal hairline */}
      <div className="absolute inset-0" style={{ background: `linear-gradient(115deg, transparent 46%, ${p.b}22 50%, transparent 54%)` }} />
      {/* monogram */}
      <span className="absolute left-8 bottom-6 font-cinzel font-bold text-7xl md:text-8xl text-white/[0.06] select-none">{initials}</span>
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 120px rgba(0,0,0,0.6)" }} />
    </div>
  );
}

export default function Portfolio() {
  const [active, setActive] = useState(null);
  const [hover, setHover] = useState(0);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-16 md:py-24 overflow-hidden">
      <div className="ambient-emerald" style={{ width: 500, height: 500, top: "5%", left: "-14%", opacity: 0.25 }} />

      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
          <Reveal>
            <Overline>Selected Work</Overline>
            <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-4 leading-tight">
              A portfolio of<br /><span className="text-gold-metallic">quiet excellence.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-poppins text-sm text-white/55 max-w-sm">
              A selection of concept and sample work that showcases our range, craft and approach. Real client case studies coming soon.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Index list */}
          <div className="lg:col-span-7 border-t border-white/10">
            {PORTFOLIO.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <button
                  data-testid={`portfolio-item-${i}`}
                  data-cursor="hover"
                  onMouseEnter={() => setHover(i)}
                  onClick={() => setActive(p)}
                  className="group w-full text-left border-b border-white/10 py-5 md:py-6 flex items-center gap-5 md:gap-8 transition-colors duration-500 hover:bg-white/[0.015] px-1 md:px-3"
                >
                  <span className={`font-inter text-sm w-7 transition-colors duration-500 ${hover === i ? "text-gk-gold" : "text-white/30"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-cinzel text-xl md:text-2xl transition-colors duration-500 ${hover === i ? "text-gk-champagne" : "text-white"}`}>
                      {p.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                      <span className="font-inter text-[10px] tracking-[0.25em] uppercase text-gk-gold/80">{p.category}</span>
                      <span className="text-white/20">·</span>
                      <span className="font-inter text-xs text-white/40">{p.industry}</span>
                      <span className="glass rounded-full px-2 py-0.5 font-inter text-[9px] tracking-[0.2em] uppercase text-white/45">Concept</span>
                    </div>
                    {/* Mobile inline abstract preview */}
                    <div className="lg:hidden mt-3 h-32 rounded-xl overflow-hidden ring-1 ring-white/10">
                      <AbstractArt index={i} title={p.title} />
                    </div>
                  </div>
                  <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${hover === i ? "bg-gk-gold text-gk-black" : "glass text-white/60"}`}>
                    <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Sticky morphing preview (desktop) */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-white/10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={hover}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0"
                  >
                    <AbstractArt index={hover} title={PORTFOLIO[hover].title} />
                  </motion.div>
                </AnimatePresence>
                <div className="absolute inset-0 flex flex-col justify-end p-8 pointer-events-none">
                  <span className="font-inter text-[10px] tracking-[0.3em] uppercase text-gk-champagne">{PORTFOLIO[hover].category}</span>
                  <h3 className="font-cinzel text-2xl text-white mt-1.5">{PORTFOLIO[hover].title}</h3>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {PORTFOLIO[hover].tags.map((t) => (
                      <span key={t} className="glass rounded-full px-3 py-1 font-inter text-[11px] text-white/70">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen modal (image-free) */}
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="portfolio-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-10 bg-gk-black/90 backdrop-blur-xl"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl max-h-[88vh] overflow-hidden rounded-3xl glass-strong ring-1 ring-gk-gold/20"
            >
              <button
                data-testid="portfolio-modal-close"
                onClick={() => setActive(null)}
                className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full glass flex items-center justify-center hover:border-gk-gold/60 transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
              <div className="h-56 md:h-72">
                <AbstractArt index={PORTFOLIO.indexOf(active)} title={active.title} />
              </div>
              <div className="p-8 md:p-12">
                <span className="font-inter text-xs tracking-[0.3em] uppercase text-gk-champagne">{active.category} · Concept</span>
                <h3 className="font-cinzel text-3xl md:text-5xl text-gold-metallic mt-3">{active.title}</h3>
                <p className="font-poppins text-white/60 leading-relaxed mt-6 max-w-2xl text-base">{active.desc}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {active.tags.map((t) => (
                    <span key={t} className="glass rounded-full px-4 py-1.5 font-inter text-xs text-white/75">{t}</span>
                  ))}
                </div>
                <div className="mt-6">
                  <p className="text-white/40 uppercase tracking-wide text-xs mb-2 font-inter">Outcome</p>
                  <p className="font-poppins text-white/70 leading-relaxed max-w-2xl">{active.outcome}</p>
                </div>
                <div className="hairline-gold w-full mt-8 mb-6" />
                <div className="flex flex-wrap gap-8 font-inter text-sm">
                  <div><p className="text-white/40 uppercase tracking-wide text-xs mb-1">Industry</p><p className="text-white">{active.industry}</p></div>
                  <div><p className="text-white/40 uppercase tracking-wide text-xs mb-1">Discipline</p><p className="text-white">{active.category}</p></div>
                  <div className="min-w-[160px]"><p className="text-white/40 uppercase tracking-wide text-xs mb-1">Technologies</p><p className="text-white">{active.tech.join(", ")}</p></div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
